import { configureStore } from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { combineReducers } from 'redux';
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux';
import { Middleware } from 'redux';
import { Action } from '@reduxjs/toolkit';
import themeReducer from './slices/themeSlice';
import settingsReducer from './slices/settingsSlice';
import windowsReducer from './slices/windowsSlice';
import stepsReducer from './slices/stepsSlice';
import analysisReducer from './slices/analysisSlice';
import notificationsReducer from './slices/notificationsSlice';
import logger from '@/services/logger';

// Combine all reducers
const rootReducer = combineReducers({
  theme: themeReducer,
  settings: settingsReducer,
  windows: windowsReducer,
  steps: stepsReducer,
  analysis: analysisReducer,
  notifications: notificationsReducer,
});

// Configure redux-persist
const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['theme', 'settings', 'steps', 'windows', 'notifications'], // Only persist these slices
  blacklist: ['analysis'], // Don't persist these slices
};

// Create the persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Define the RootState type
export type RootState = ReturnType<typeof rootReducer>;

// Type guard to check if an object is a Redux action
function isReduxAction(action: unknown): action is Action & { payload?: unknown } {
  return typeof action === 'object' && action !== null && 'type' in action;
}

// Custom logging middleware with proper TypeScript typing
const loggerMiddleware: Middleware = store => next => action => {
  // Log the action
  if (import.meta.env.DEV && isReduxAction(action)) {
    logger.debug(`Action: ${action.type}`, {
      module: 'redux',
      data: { payload: action.payload },
    });
  }

  // Call the next dispatch method in the middleware chain
  const result = next(action);

  // Log the current state after action was handled
  if (import.meta.env.DEV) {
    logger.debug('State updated', {
      module: 'redux',
      data: { store: store.getState() },
    });
  }

  return result;
};

// Configure the store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(loggerMiddleware),
  devTools: import.meta.env.DEV,
});

// Create the persistor
export const persistor = persistStore(store);

// Export types
export type AppDispatch = typeof store.dispatch;

// Typed hooks
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
